import PhoneForm from '@liveauctioneers/caterwaul-components/lib/Forms/PhoneForm/PhoneForm';
import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { Modal } from '@liveauctioneers/hammer-ui-core/modal';
import {
    CloseButton,
    ModalActions,
    ModalContent,
    ModalHeader,
    ModalTitle,
} from '@liveauctioneers/hammer-ui-core/modal/components';
import { Caption, H5 } from '@liveauctioneers/hammer-ui-core/text';
import { cn } from '@liveauctioneers/hammer-ui-theme/cn';
import { Controller, useFormContext } from 'react-hook-form';

import {
    TrackEnableSmsOnSavePromptActionTaken,
    useTrackEnableSmsOnSavePrompt,
} from '@/utils/amplitude/analytics/notifications';
import { getTermsAndConditionsUrl } from '@/utils/urls';

import { EnableSmsOnSaveForm } from '../../EnableSmsOnSavePrompt';

type Props = {
    onCloseSaveWidget: (isModal?: boolean) => void;
    setShow: (show: boolean) => void;
    show: boolean;
};

export const EnableSmsOnSaveModal = ({ onCloseSaveWidget, setShow, show }: Props) => {
    const { formatMessage } = useIntl();

    const trackEnableSmsOnSavePrompt = useTrackEnableSmsOnSavePrompt();
    const {
        control,
        formState: { errors },
        setValue,
        watch,
    } = useFormContext<EnableSmsOnSaveForm>();
    const isFormSubmitAttempted = watch('isFormSubmitAttempted');
    const isValidPhone = watch('isValid');
    const isSubmitLoading = watch('isSubmitLoading');

    const hasPhoneInputError = (Boolean(errors.phoneData?.message) || !isValidPhone) && isFormSubmitAttempted;

    return (
        <Modal
            closeModalOnBackdropClick={false}
            handleClose={(reason?: string) => {
                if (reason === 'close') {
                    trackEnableSmsOnSavePrompt({ actionTaken: TrackEnableSmsOnSavePromptActionTaken.Closed });
                }
                setShow(false);
            }}
            preventScrollWhileOpen
            show={show}
            type="composable"
        >
            <CloseButton />
            <ModalHeader>
                <ModalTitle>{formatMessage({ id: 'enableSmsOnSave.title' })}</ModalTitle>
            </ModalHeader>
            <ModalContent>
                <div className="mt-sm flex flex-col gap-sm py-md">
                    <H5>{formatMessage({ id: 'enableSmsOnSave.getTextWhenItIsTimeToBidLive' })}</H5>
                    <div>
                        <Controller
                            control={control}
                            name="phoneData"
                            render={({ field: { onChange, value } }) => (
                                //TODO: migrate to use the new phone form component from Hammer UI https://auctiontechnologygroup.atlassian.net/browse/LAAC-312
                                //Hiding spacer under the input in the DOM unless there is an error
                                <div
                                    className={cn(
                                        hasPhoneInputError
                                            ? ''
                                            : "[&_[data-testid='nameContactFormPhoneInput-telInput-spacer']]:hidden"
                                    )}
                                >
                                    <PhoneForm
                                        errorMessage={formatMessage({ id: 'enableSmsOnSave.phoneNumberError' })}
                                        onChange={(phoneForm, valid) => {
                                            onChange(phoneForm);
                                            setValue('isValid', valid);
                                            setValue('isFormSubmitAttempted', false);
                                        }}
                                        phoneLabel={null}
                                        phoneNumber={value}
                                        placeholder={formatMessage({
                                            id: 'enableSmsOnSave.phoneNumberPlaceholder',
                                        })}
                                        showCountryCode
                                        submitted={isFormSubmitAttempted}
                                        testid="nameContactFormPhoneInput"
                                    />
                                </div>
                            )}
                        />

                        <div className="mt-2xs">
                            <Caption className="text-text-secondary">
                                <FormattedMessage
                                    id="leadingBidder.captions.terms"
                                    values={{
                                        termsLink: (
                                            <Link
                                                className="font-inter text-xs font-normal text-link-primary no-underline hover:no-underline"
                                                href={getTermsAndConditionsUrl('tcpa-consent-privacy')}
                                                key="leading-bid-terms-and-conditions-link"
                                                target="_blank"
                                            >
                                                <FormattedMessage id="terms_and_conditions" />
                                            </Link>
                                        ),
                                    }}
                                />
                            </Caption>
                        </div>
                    </div>
                </div>
            </ModalContent>
            <ModalActions>
                <Button
                    className="xsMin:min-w-[158px] mr-sm w-full"
                    data-testid="noThanksButton"
                    disabled={isSubmitLoading}
                    onClick={() => {
                        trackEnableSmsOnSavePrompt({ actionTaken: TrackEnableSmsOnSavePromptActionTaken.Rejected });
                        onCloseSaveWidget(true);
                    }}
                    variant="outlined"
                >
                    {formatMessage({ id: 'enableSmsOnSave.noThanks' })}
                </Button>
                <Button
                    className="xsMin:min-w-[158px] w-full"
                    data-testid="textMeButton"
                    form="enableSmsOnSaveForm"
                    loading={isSubmitLoading}
                    type="submit"
                >
                    {formatMessage({ id: 'enableSmsOnSave.textMe' })}
                </Button>
            </ModalActions>
        </Modal>
    );
};
